import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { SideNavContext } from './index.context';
// import { Icon } from '../../Atoms'; // DOC: Used by collapse feature, keep it for a while

import * as Styles from './index.styles';

const {
    StyledSideNav,
    StyledNavItemContent,
    // StyledCollapseButton, // DOC: Used by collapse feature, keep it for a while
    StyledLogoWrapper,
    StyledLogo,
    StyledScrollable,
} = Styles;

const regularLogoSize = {
    height: '38px',
    width: '138px',
};

const collapsedLogoSize = {
    height: '24px',
    width: '24px',
};

export const SideNav = ({
    defaultSelected,
    width,
    mobileBreakpoint,
    onSelect,
    isCollapsable,
    logoOpen,
    logoClosed,
    logoCallback,
    collapseCallback,
    children,
    collapsibleSubNavItem,
    ...otherProps
}) => {
    const [selected, setSelected] = useState({ id: defaultSelected });
    const [navItemChildren, setNavItemChildren] = useState(null);
    const [mobile, setMobile] = useState(window.innerWidth - (mobileBreakpoint || 0) <= 0);
    const [collapsedMenu, setCollapsedMenuHook] = useState(false);
    const ref = useRef();

    const [isMenuOpen, setIsMenuOpen] = useState(false);

    React.useEffect(() => {
        const hasNavItemChildren = !!navItemChildren;

        setIsMenuOpen(() => !hasNavItemChildren);
    }, [navItemChildren]);

    const setCollapsedMenu = newValue => {
        setCollapsedMenuHook(newValue);
        collapseCallback(newValue);
        const updateCollapsedMenu = typeof newValue === 'boolean' ? newValue : !collapsedMenu;
        if (updateCollapsedMenu) {
            document.body.style.marginLeft = '60px';
        } else {
            document.body.style.marginLeft = `${width || '230'}px`;
        }
    };

    const hasAssociatedChildren = selectedChildren => {
        return !!navItemChildren && !!selectedChildren;
    };

    const toggleMenu = selectedChildren => {
        // If a NavItem has associatedChildren, a specific sub menu will be rendered
        // We want to toggle the menu, so we associate || disassociate children
        // to control the sub menu rendering.

        const newChildrenState = hasAssociatedChildren(selectedChildren) ? null : selectedChildren;
        setTimeout(setNavItemChildren(newChildrenState), 600);
    };

    const onSelectNavItem = (evt, { id, children: selectedChildren }, onClick = () => {}) => {
        const nextSelected = selected && id !== selected.id ? id : undefined;

        onClick(isMenuOpen);
        if (!selectedChildren) {
            onSelect(evt, nextSelected);
            setSelected({ id: nextSelected });

            toggleMenu(null);
        } else {
            toggleMenu(selectedChildren);
        }
    };

    const handleResize = () => {
        const diff = window.innerWidth - (mobileBreakpoint || 0);
        if (diff <= 0) {
            setMobile(true);
        } else {
            setMobile(false);
        }
    };

    useEffect(() => {
        handleResize();
        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        setSelected({ id: defaultSelected });
    }, [defaultSelected]);

    useEffect(() => {
        if (mobile) {
            document.body.style.marginLeft = 'initial';
            document.body.style.marginBottom = '60px';
        } else {
            if (collapsedMenu) {
                document.body.style.marginLeft = '60px';
            } else {
                document.body.style.marginLeft = `${width || '230'}px`;
            }
            document.body.style.marginBottom = 'initial';
        }

        if (navItemChildren) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'initial';
        }

        return () => {
            document.body.style.marginLeft = 'initial';
            document.body.style.marginBottom = 'initial';
            document.body.style.overflow = 'initial';
        };
    }, [mobile, selected, navItemChildren]);

    return (
        <SideNavContext.Provider
            value={{
                selected,
                mobile,
                collapsedMenu,
                setCollapsedMenu,
                onSelect: onSelectNavItem,
                collapsibleSubNavItem,
            }}
        >
            <StyledSideNav
                ref={ref}
                width={width}
                mobile={mobile}
                collapsedMenu={collapsedMenu}
                {...otherProps}
            >
                {logoOpen && !mobile && (
                    <StyledLogoWrapper>
                        <StyledLogo
                            src={collapsedMenu ? logoClosed : logoOpen}
                            alt="menu-logo"
                            height={
                                collapsedMenu ? collapsedLogoSize.height : regularLogoSize.height
                            }
                            width={collapsedMenu ? collapsedLogoSize.width : regularLogoSize.width}
                            onClick={logoCallback}
                        />
                    </StyledLogoWrapper>
                )}
                {/* DOC: The collapse button will be hidden for a while, in the future it should be removed */}
                {/* {isCollapsable && !mobile && (
                    <StyledCollapseButton
                        onClick={() => setCollapsedMenu(prev => !prev)}
                        collapsedMenu={collapsedMenu}
                    >
                        <Icon icon={['fas', 'angle-left']} />
                    </StyledCollapseButton>
                )} */}
                {!mobile ? <StyledScrollable>{children}</StyledScrollable> : children}
            </StyledSideNav>
            <StyledNavItemContent visible={!!navItemChildren}>
                {navItemChildren || null}
            </StyledNavItemContent>
        </SideNavContext.Provider>
    );
};

SideNav.propTypes = {
    defaultSelected: PropTypes.string,
    width: PropTypes.number,
    mobileBreakpoint: PropTypes.number,
    shadow: PropTypes.bool,
    isCollapsable: PropTypes.bool,
    logoOpen: PropTypes.string,
    logoClosed: PropTypes.string,
    logoCallback: PropTypes.func,
    collapseCallback: PropTypes.func,
    onSelect: PropTypes.func,
    children: PropTypes.node.isRequired,
    collapsibleSubNavItem: PropTypes.bool,
};

/* istanbul ignore next */
SideNav.defaultProps = {
    width: undefined,
    mobileBreakpoint: 576,
    defaultSelected: undefined,
    shadow: false,
    isCollapsable: false,
    logoOpen: undefined,
    logoClosed: undefined,
    logoCallback: () => null,
    collapseCallback: () => null,
    onSelect: () => null,
    collapsibleSubNavItem: false,
};

SideNav.Styles = Styles;

export * from './SideNavItem';
export * from './SideNavSection';
export * from './CollapsableNavItem';
